import React from 'react';
import { Container, Body5 } from '@ui';
import { ResultContext, RUNNING_STATUS } from '@/contexts/ResultContext';
import { AnswerConfigContext } from '@/contexts/AnswerConfigContext';
import { AnswerFormatter } from '@classes/AnswerFormatter';
import { isAgentOutputValid, AgentOutput, completePartialJson } from '@features/agentOutput';
import { AnswerBody } from './AnswerBody';

const answerFormatter = new AnswerFormatter();

function tryCompleteAndValidate(answer) {
  const completed = completePartialJson(answer);
  if (!completed) return null;
  try {
    const json = JSON.parse(completed);
    if (json.answer || json.answer_list) return completed;
  } catch {
    console.log('Error parsing completed JSON', completed);
    return null;
  }
  return null;
}

const StreamingAgentAnswer = ({ answer }) => {
  const completedOutput = React.useMemo(() => tryCompleteAndValidate(answer), [answer]);

  if (completedOutput) {
    return <AgentOutput output={completedOutput} isStreaming />;
  }

  if (answer === null || answer === '') {
    return <Body5 isLowContrast></Body5>;
  }

  console.log('Non output', answer);
  return (
    <Container>
      <Body5 isLowContrast>Building agent output...</Body5>
      <Container top={16}>
        <Body5 isLowContrast>{answer.length > 200 ? answer.slice(0, 200) + '...' : answer}</Body5>
      </Container>
    </Container>
  );
};

export const AgentAnswer = () => {
  const { ignoreLatex, enableLinks, enableMoreDetail } = React.useContext(AnswerConfigContext);
  const { runningStatus, answer } = React.useContext(ResultContext);

  const isStreaming = runningStatus === RUNNING_STATUS.RUNNING;
  const isStructuredOutput = !isStreaming && isAgentOutputValid(answer);
  const formattedAnswer = React.useMemo(() => {
    if (isStreaming || isStructuredOutput || !answer) return null;
    return answerFormatter.formatResult(answer, ignoreLatex);
  }, [answer, runningStatus, isStructuredOutput, ignoreLatex]);

  return (
    <Container>
      {(runningStatus === RUNNING_STATUS.ERROR || runningStatus === RUNNING_STATUS.RETRYING_ERROR) && (
        <Container top={24} bottom={24}>
          <Body5 bold>Something went wrong. Attempting to fix...</Body5>
        </Container>
      )}
      {isStreaming ? (
        <StreamingAgentAnswer answer={answer} isStreaming />
      ) : isStructuredOutput ? (
        <AgentOutput output={answer} isStreaming={isStreaming} />
      ) : (
        <AnswerBody
          isStreaming={isStreaming}
          answer={formattedAnswer}
          enableLinks={enableLinks}
          enableMoreDetail={enableMoreDetail}
          ignoreLatex={ignoreLatex}
        />
      )}
    </Container>
  );
};
